package co.maytes.standupbot.dashboard.features.standupruns

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.widgets.Badge
import co.maytes.standupbot.dashboard.components.widgets.BadgeTone
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonSize
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.features.settings.IntegrationsCopy
import co.maytes.standupbot.dashboard.features.setup.ReconnectRoutes
import co.maytes.standupbot.dashboard.features.setup.rememberReconnectLauncher
import co.maytes.standupbot.dashboard.stores.rememberAuthStore
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.design.StatusHue
import co.maytes.standupbot.shared.dto.standuprun.RunCauseDto
import co.maytes.standupbot.shared.dto.standuprun.StandupRunWarningDto
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.window
import kotlinx.coroutines.launch

object RunCauseRules {
    val incompleteCodes = setOf("linear-user-fetch-failed", "github-user-fetch-failed")

    fun incompleteWarnings(warnings: List<StandupRunWarningDto>): List<StandupRunWarningDto> =
        warnings.filter { it.code in incompleteCodes }

    fun showsIncompleteNotice(
        warnings: List<StandupRunWarningDto>,
        cause: RunCauseDto?,
    ): Boolean = incompleteWarnings(warnings).isNotEmpty() && cause != null

    fun providerLabel(cause: RunCauseDto): String = IntegrationsCopy.displayName(cause.provider)

    fun canAct(
        cause: RunCauseDto,
        isAdmin: Boolean,
    ): Boolean = isAdmin && cause.fix != null
}

@Composable
fun CauseAction(cause: RunCauseDto) {
    val auth = rememberAuthStore()
    if (!RunCauseRules.canAct(cause, auth.isAdmin)) return
    val fix = cause.fix ?: return
    val launcher = rememberReconnectLauncher()
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()
    var launching by remember { mutableStateOf(false) }
    Button(
        label = if (launching) "Redirecting…" else fix.label,
        variant = ButtonVariant.Secondary,
        size = ButtonSize.Sm,
        enabled = !launching,
        onClick = {
            if (!cause.launchesReconnect) {
                ctx.router.navigateTo(fix.path)
                return@Button
            }
            val workspaceId = auth.currentUser?.workspace?.id ?: return@Button
            scope.launch {
                launching = true
                val started =
                    launcher.launch(
                        provider = cause.provider,
                        workspaceId = workspaceId,
                        providerName = RunCauseRules.providerLabel(cause),
                        returnTo = ReconnectRoutes.returnPathFor(window.location.pathname),
                        navigate = { path -> ctx.router.navigateTo(path) },
                    )
                if (!started) launching = false
            }
        },
    )
}

@Composable
fun ProviderCauseChip(cause: RunCauseDto?) {
    if (cause == null) return
    Badge(label = RunCauseRules.providerLabel(cause), tone = BadgeTone.Warning)
}

@Composable
fun IncompleteRunNotice(
    warnings: List<StandupRunWarningDto>,
    cause: RunCauseDto?,
) {
    if (!RunCauseRules.showsIncompleteNotice(warnings, cause) || cause == null) return
    val warning = Palette.status(StatusHue.Warning)
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(Spacing.md)
            .borderRadius(Radius.md)
            .gap(Spacing.xs)
            .styleModifier {
                property("background", warning.subtle.cssRgb())
                property("border", "1px solid ${warning.border.cssRgb()}")
            },
    ) {
        SpanText(
            text = "This run is incomplete",
            modifier = Modifier.fontSize(Typography.body.size).fontWeight(600).color(warning.onSubtle),
        )
        SpanText(
            text = cause.headline + ". Some sections below may be missing activity.",
            modifier = Modifier.fontSize(Typography.bodySmall.size).color(warning.onSubtle),
        )
        RunCauseRules.incompleteWarnings(warnings).forEach { warningDto ->
            SpanText(
                text = warningDto.message,
                modifier = Modifier.fontSize(Typography.caption.size).color(Palette.textSecondary),
            )
        }
        Row(modifier = Modifier.margin(top = Spacing.xs), verticalAlignment = Alignment.CenterVertically) { CauseAction(cause) }
    }
}
